import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, UserCircle, X, Facebook, Instagram } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface FileObject {
  id: string;
  name: string;
  // Add other necessary properties
}

interface PaymentNotification {
  id: string;
  status: string;
  // Add other necessary properties
}

interface Profile {
  role: string;
  prenume: string;
  nume: string;
}

const ALLOWED_EMAILS = [
  'ovidiu.mudura@yahoo.com',
  'tudorbolos2001@yahoo.com',
  'mihaicojocarulaboncz@yahoo.com',
  'sergiumurg@yahoo.com',
  'sergiu.murg@decizieoncologica.ro',
  'viman.diana@yahoo.ro',
  'flaviuscoser@gmail.com'
];

interface HeaderProps {
  className?: string;
}

export default function Header({ className = '' }: HeaderProps) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState<FileObject[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [standardPaymentLoading, setStandardPaymentLoading] = useState(false);
  const [asociatiePaymentLoading, setAsociatiePaymentLoading] = useState(false);
  const [paymentNotification, setPaymentNotification] = useState<PaymentNotification | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [requestingConsultation, setRequestingConsultation] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [resendingLetter, setResendingLetter] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        setIsAuthorizedUser(ALLOWED_EMAILS.includes(session.user.email || ''));
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        setIsAuthorizedUser(ALLOWED_EMAILS.includes(session.user.email || ''));
      } else {
        setProfile(null);
        setIsAuthorizedUser(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('role, prenume, nume')
      .eq('id', userId)
      .single();
    setProfile(data);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setIsOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  return (
    <header className={`bg-[#024994] shadow-lg w-full ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center h-12 sm:h-16">
              <img src="/logo.png" alt="Decizie Oncologica" className="h-full w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-2">
              <a
                href="https://facebook.com/decizieoncologica/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-[#D3D1CF] transition p-2"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/decizieoncologica/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-[#D3D1CF] transition p-2"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-white hover:text-[#D3D1CF] transition">Acasă</Link>
            <Link to="/despre" className="text-white hover:text-[#D3D1CF] transition">Despre Noi</Link>
            {user && (profile?.role === 'pacient' || profile?.role === 'admin') && (
              <Link to="/pacient" className="text-white hover:text-[#D3D1CF] transition">Pacient</Link>
            )}
            {user && (profile?.role === 'doctor' || profile?.role === 'admin') && (
              <>
                <Link to="/doctor" className="text-white hover:text-[#D3D1CF] transition">Panou pacienți</Link>
                <Link to="/doctor/appointments" className="text-white hover:text-[#D3D1CF] transition">Calendar programări</Link>
                <Link to="/doctor/medical-letters" className="text-white hover:text-[#D3D1CF] transition">Scrisori medicale</Link>
              </>
            )}
            {user && profile?.role === 'pacient' && (
              <Link to="/recenzii" className="text-white hover:text-[#D3D1CF] transition">Recenzii</Link>
            )}
            {isAuthorizedUser && (
              <Link to="/recenzii" className="text-white hover:text-[#D3D1CF] transition">Recenzii</Link>
            )}
          </nav>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <button 
                onClick={() => setIsOpen(!isOpen)}
                className="text-white hover:text-[#D3D1CF] transition flex items-center gap-2"
              >
                <UserCircle className="w-6 h-6" />
                <span className="hidden sm:inline whitespace-nowrap">
                  {user ? (profile ? `${profile.prenume} ${profile.nume}` : 'Contul Meu') : 'Autentificare'}
                </span>
              </button>

              {isOpen && (
                <div 
                  className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                  onClick={() => setIsOpen(false)}
                >
                  {user ? (
                    <>
                      {profile?.role === 'pacient' && (
                        <Link
                          to="/pacient"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Pagina Pacient
                        </Link>
                      )}
                      {profile?.role === 'doctor' && (
                        <Link
                          to="/doctor"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Panou pacienți
                        </Link>
                      )}
                      {isAuthorizedUser && (
                        <>
                          <Link
                            to="/financiar"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Financiar
                          </Link>
                          <Link
                            to="/plati"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Plăți
                          </Link>
                        </>
                      )}
                      <Link
                        to="/schimba-parola"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Schimbă parola
                      </Link>
                {[
                  'ovidiu.mudura@yahoo.com',
                  'tudorbolos2001@yahoo.com',
                  'mihaicojocarulaboncz@yahoo.com', 
                  'sergiumurg@yahoo.com',
                  'sergiu.murg@decizieoncologica.ro',
                  'viman.diana@yahoo.ro',
                  'flaviuscoser@gmail.com'
                ].includes(user?.email?.toLowerCase() || '') && (
                  <>
                    <a
                      href="https://drive.google.com/drive/folders/1jcg92t_mO5-L-m-_5dGIXUqPru9VWB2R?usp=drive_link"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Link drive
                    </a>
                    <Link
                      to="/statistici"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Statistici
                    </Link>
                    <Link
                      to="/visitcard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Cărți de vizită
                    </Link>
                  </>
                )}
                <button
                  onClick={handleSignOut}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  Deconectare
                </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Înregistrare
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white hover:text-[#D3D1CF] transition"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#024994] w-full">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/" 
                className="text-white hover:text-[#D3D1CF] transition"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Acasă
              </Link>
              <Link 
                to="/despre" 
                className="text-white hover:text-[#D3D1CF] transition"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Despre Noi
              </Link>
              {user && (profile?.role === 'pacient' || profile?.role === 'admin') && (
                <Link 
                  to="/pacient" 
                  className="text-white hover:text-[#D3D1CF] transition"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Pacient
                </Link>
              )}
              {user && (profile?.role === 'doctor' || profile?.role === 'admin') && (
                <>
                  <Link 
                    to="/doctor" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Panou pacienți
                  </Link>
                  <Link 
                    to="/doctor/appointments" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Calendar programări
                  </Link>
                  <Link 
                    to="/doctor/medical-letters" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Scrisori medicale
                  </Link>
                </>
              )}
              {user && profile?.role === 'pacient' && (
                <Link 
                  to="/recenzii" 
                  className="text-white hover:text-[#D3D1CF] transition"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Recenzii
                </Link>
              )}
              {isAuthorizedUser && (
                <>
                  <Link 
                    to="/recenzii" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Recenzii
                  </Link>
                  <Link 
                    to="/financiar" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Financiar
                  </Link>
                  <Link 
                    to="/plati" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Plăți
                  </Link>
                  <a
                    href="https://drive.google.com/drive/folders/1jcg92t_mO5-L-m-_5dGIXUqPru9VWB2R?usp=drive_link"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Link drive
                  </a>
                  <Link 
                    to="/statistici" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Statistici
                  </Link>
                  <Link 
                    to="/visitcard" 
                    className="text-white hover:text-[#D3D1CF] transition"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Cărți de vizită
                  </Link>
                </>
              )}
              <div className="flex gap-4 pt-2">
                <a
                  href="https://facebook.com/decizieoncologica/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#D3D1CF] transition"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://instagram.com/decizieoncologica/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#D3D1CF] transition"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
